import { deleteApi, getApi, postApi, putApi } from "../helpers/auth_helpers";

const base = process.env.REACT_APP_SERVERURL || "";

/** Team-only portrait links (signed, about an hour) by user id, for the ids that have one. */
export const getArtisanPortraits = async (userIds: string[]): Promise<Record<string, string>> => {
  const ids = Array.from(new Set(userIds.filter(Boolean)));
  if (!ids.length) return {};
  const res: any = await getApi(`${base}/artisan-portraits?ids=${encodeURIComponent(ids.join(","))}`);
  return res?.success && res?.data ? (res.data as Record<string, string>) : {};
};

/**
 * The portrait goes up through the HKP media service like any product photo (master, the five
 * delivery sizes, the original archived in the background, all in private storage) and its media
 * id is attached to the artisan. There is no other path: the legacy upload bucket is public.
 */
export const uploadArtisanPortraitMedia = async (userId: string, file: File): Promise<string | null> => {
  const { mediaUploadManager } = await import("../media/createMediaUploadManager");
  const result = await mediaUploadManager.upload(file, "ARTISAN_PORTRAIT");
  result.disposePreview();
  const saved: any = await putApi(`${base}/artisan-portraits/${userId}`, { mediaId: result.mediaId });
  if (!saved?.success) throw new Error(saved?.message || "The photo didn't save");
  return saved.data?.portraitUrl || null;
};

export type PublicConsentStatus = "NONE" | "GRANTED" | "WITHDRAWN";

export type PublicConsent = {
  status: PublicConsentStatus;
  grantedAt?: string | null;
  withdrawnAt?: string | null;
  hasPhoto?: boolean;
};

/**
 * Whether each artisan agreed to be shown on this studio's certificates. `enabled` is false while
 * the feature is off on the server; then nothing about it is shown.
 */
export const getPublicConsent = async (
  userIds: string[]
): Promise<{ enabled: boolean; artisans: Record<string, PublicConsent> }> => {
  const ids = Array.from(new Set(userIds.filter(Boolean)));
  if (!ids.length) return { enabled: false, artisans: {} };
  const res: any = await getApi(`${base}/artisan-portraits/public-consent?ids=${encodeURIComponent(ids.join(","))}`);
  if (!res?.success || !res?.data) return { enabled: false, artisans: {} };
  return { enabled: Boolean(res.data.enabled), artisans: (res.data.artisans || {}) as Record<string, PublicConsent> };
};

/** The artisan agreed in person and is 18 or over; the studio records it. */
export const recordPublicConsent = async (userId: string): Promise<PublicConsent> => {
  const res: any = await postApi(`${base}/artisan-portraits/${userId}/public-consent`, {
    artisanAgreed: true,
    confirmAdult: true,
  });
  if (!res?.success) throw new Error(res?.message || "Not saved. Try again");
  return res.data as PublicConsent;
};

export const withdrawPublicConsent = async (userId: string, reason: string): Promise<PublicConsent> => {
  const res: any = await deleteApi(`${base}/artisan-portraits/${userId}/public-consent`, { reason });
  if (!res?.success) throw new Error(res?.message || "Not saved. Try again");
  return res.data as PublicConsent;
};
